/**
 * iter409e — GlobalAutoNew
 *
 * Global listener for `?new=1` URL query param. When detected on an admin
 * route, waits for the module's "+ Nová X" primary button to mount, then
 * synthesises a click on it. Strips the param so a F5 refresh won't re-open.
 *
 * This is the cheap "single-file" alternative to adding `useAutoNew()` to
 * every individual module. Modules just keep their existing `new-*-btn`
 * data-testid attributes and this listener takes care of the rest.
 *
 * Triggered by Admin2 desktop's icon context menu "+ Nový {label}" item:
 *   openModule(key, { new: "1" }) → iframe URL gets `?...&new=1`
 *
 * Common button selectors searched in order:
 *   [data-testid^="new-"][data-testid$="-btn"]
 *
 * Module-specific overrides (when several "new-*-btn" exist on the page):
 *   /admin/customers   →  [data-testid="new-customer-btn"]
 *   /admin/quotes      →  [data-testid="new-quote-btn"]
 *   /admin/projects    →  [data-testid="new-project-btn"]
 *   /admin/submissions →  [data-testid="new-submission-btn"]
 *   /admin/invoices    →  [data-testid="new-invoice-btn"]
 *   /admin/claims      →  [data-testid="new-claim-btn"]
 *   etc.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Per-path preferred button testid (when ambiguous)
const PATH_TO_TESTID = {
  "/admin/customers":        "new-customer-btn",
  "/admin/quotes":           "new-quote-btn",
  "/admin/projects":         "new-project-btn",
  "/admin/submissions":      "new-submission-btn",
  "/admin/invoices":         "new-invoice-btn",
  "/admin/claims":           "new-claim-btn",
  "/admin/service-orders":   "new-service-btn",
  "/admin/tasks":            "new-task-btn",
  "/admin/activity":         "new-activity-btn",
  "/admin/items-catalog":    "new-item-btn",
  "/admin/contracts":        "new-contract-btn",
  "/admin/protocols":        "new-protocol-btn",
  "/admin/rentals":          "new-rental-btn",
  "/admin/documents":        "new-doc-btn",
  "/admin/calendar":         "new-event-btn",
  "/admin/subsidies":        "new-subsidy-btn",
};

export default function GlobalAutoNew() {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("new") !== "1") return;
    if (!location.pathname.startsWith("/admin/")) return;

    // Strip the param so a refresh won't re-trigger
    const url = new URL(window.location.href);
    url.searchParams.delete("new");
    window.history.replaceState({}, "", url.toString());

    // Pick the most specific testid for this path, fallback to first new-*-btn
    const preferred = PATH_TO_TESTID[location.pathname] ||
      Object.entries(PATH_TO_TESTID).find(([p]) => location.pathname.startsWith(p))?.[1] ||
      null;

    // Poll for the button to appear (lazy-load may take a moment)
    let attempts = 0;
    const maxAttempts = 30;  // 30 × 100ms = 3s
    const tryClick = () => {
      attempts += 1;
      let btn = null;
      if (preferred) {
        btn = document.querySelector(`[data-testid="${preferred}"]`);
      }
      if (!btn) {
        // Fallback: first visible new-*-btn excluding sub-buttons like
        // new-row-btn, new-item-row-btn (inside forms)
        const candidates = Array.from(document.querySelectorAll('[data-testid^="new-"][data-testid$="-btn"]'));
        btn = candidates.find((el) => {
          const t = el.getAttribute("data-testid") || "";
          // Skip nested "new-X-row-btn" or "new-X-item-btn" (those add rows inside forms)
          if (/-row-|-item-/.test(t)) return false;
          // Must be visible (offsetParent !== null is a quick visibility check)
          return el.offsetParent !== null;
        }) || null;
      }
      if (btn) {
        try { btn.click(); } catch (_) { /* noop */ }
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(tryClick, 100);
      }
    };
    // Small initial delay so the page mounts its header before we look
    const initialTimer = setTimeout(tryClick, 200);
    return () => clearTimeout(initialTimer);
  }, [location.pathname, location.search]);

  return null;
}
